import { calcClientDemographics, calculateAge, zipCodeToState } from '@shared';
import {
  carescoutAvailableStates,
  manhattanLifeOmniFlexStcStates,
  MANHATTAN_LIFE_STC_MAX_ISSUE_AGE,
  MANHATTAN_LIFE_STC_MIN_ISSUE_AGE,
  assetCare20PayStates,
  assetCare20PaySingleCohorts,
  assetCare20PayJointCouples,
  type AssetCare20PayCohort,
} from '@shared/carrierStateAvailability';

export const MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY = 40;
export const MIN_ELIGIBLE_AGE_NYL_10PAY_SINGLE_PAY = 30;
export const MAX_ELIGIBLE_AGE_CARESCOUT = 65;

export const TRADITIONAL_LTC_PAYMENT_OPTIONS = ['Monthly', 'Annually'];

/**
 * Selects the dedicated ManhattanLife short-term care view. Not a billing
 * frequency — the product is monthly-only — so it never participates in the
 * life or traditional-LTC quote pipelines.
 */
export const SHORT_TERM_CARE_PAYMENT_OPTION = 'Short-Term Care';

/**
 * ManhattanLife OmniFlex STC is billed monthly only, so it makes the Monthly
 * payment option relevant on its own — separately from CareScout, which is the
 * only other product using Monthly and which sells to a different set of states
 * and a lower age ceiling. Gating the two together hid the product entirely for
 * clients eligible for it but not for CareScout.
 *
 * State/age availability rather than "are there rows": the payment option has to
 * render before any quote fetch can happen.
 */
export function getShowShortTermCareOptions(
  quoteApplyManhattanLifeFlag: boolean | undefined,
  clientZipCode: string,
  clientBirthDate: string | Date,
) {
  if (!quoteApplyManhattanLifeFlag) return false;
  const state = zipCodeToState(clientZipCode);
  const clientAge = calculateAge(new Date(clientBirthDate));
  return (
    manhattanLifeOmniFlexStcStates.includes(state.stateAbbreviation as never) &&
    clientAge >= MANHATTAN_LIFE_STC_MIN_ISSUE_AGE &&
    clientAge <= MANHATTAN_LIFE_STC_MAX_ISSUE_AGE
  );
}

/**
 * Whether the client falls inside the slice of Asset Care 20-Pay we hold rows for.
 *
 * Derived through calcClientDemographics — the same helper buildQuoteQueryPayload
 * uses to build the request — so the gate and the query can never disagree about
 * the client's age, gender or state. Checking the raw survey fields here instead
 * would reintroduce exactly that drift.
 *
 * The two flags are reported separately because they answer different questions
 * and the tabs ask them separately: single life needs only this client, joint
 * life needs this client AND the partner to be the one couple that was scraped.
 * Collapsing them into a single "eligible" and showing 20-Pay on both tabs put
 * the option in front of clients whose active tab could never return a row.
 */
export function getAssetCare20PayEligibility(
  intakeSurvey: Partial<IntakeSurvey>,
  partnerIntakeSurvey?: Partial<IntakeSurvey> | null,
): {
  isSingleEligible: boolean;
  isJointEligible: boolean;
  isEligible: boolean;
} {
  const { primaryAge, primaryGender, state } = calcClientDemographics(intakeSurvey);

  const stateIsCovered =
    !!state && assetCare20PayStates.includes(state.stateAbbreviation as never);

  const is = (cohort: AssetCare20PayCohort, age: number, gender: string) =>
    cohort.age === age && cohort.gender === gender;

  const isSingleEligible =
    stateIsCovered &&
    assetCare20PaySingleCohorts.some(cohort => is(cohort, primaryAge, primaryGender));

  // Mirrors the joint quote SQL, which accepts the couple in either order.
  const partner = partnerIntakeSurvey ? calcClientDemographics(partnerIntakeSurvey) : null;
  const isJointEligible =
    stateIsCovered &&
    !!partner &&
    assetCare20PayJointCouples.some(
      couple =>
        (is(couple.primary, primaryAge, primaryGender) &&
          is(couple.secondary, partner.primaryAge, partner.primaryGender)) ||
        (is(couple.primary, partner.primaryAge, partner.primaryGender) &&
          is(couple.secondary, primaryAge, primaryGender)),
    );

  return { isSingleEligible, isJointEligible, isEligible: isSingleEligible || isJointEligible };
}

export function getShowTraditionalLTCOptions(
  clientZipCode: string,
  clientBirthDate: string | Date,
) {
  const state = zipCodeToState(clientZipCode);
  const clientAge = calculateAge(new Date(clientBirthDate));
  return (
    carescoutAvailableStates.includes(state.stateAbbreviation as any) &&
    clientAge <= MAX_ELIGIBLE_AGE_CARESCOUT
  );
}

/**
 * Guards a payment option restored from saved advisor defaults or a query
 * param. Those values travel across clients, and 'Short-Term Care' is only
 * offered to STC-eligible ones — restoring it elsewhere would select an option
 * the dropdown never renders, leaving a blank selector with no way back.
 */
export function clampPaymentOptionToEligibility<T extends string | undefined>(
  providedPaymentOption: T,
  isShortTermCareEligible: boolean,
  is20PayEligible = false,
): T | undefined {
  if (providedPaymentOption === SHORT_TERM_CARE_PAYMENT_OPTION && !isShortTermCareEligible) {
    return undefined;
  }
  // Same hazard as Short-Term Care: 20-Pay is offered to a narrow set of cohorts,
  // so a saved default or query param carried over from an eligible client would
  // select an option this client's dropdown never renders.
  if (providedPaymentOption === '20-Pay' && !is20PayEligible) {
    return undefined;
  }
  return providedPaymentOption;
}

export function getShowSinglePayAnd10Pay(
  hideSinglePayAnd10PayFlag: boolean | undefined,
  quoteApplyNewYorkLifeFlag: boolean | undefined,
  clientBirthDate: string | Date,
) {
  const clientAge = calculateAge(new Date(clientBirthDate));
  const minimumEligibleAge = quoteApplyNewYorkLifeFlag
    ? MIN_ELIGIBLE_AGE_NYL_10PAY_SINGLE_PAY
    : MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY;
  return !hideSinglePayAnd10PayFlag && clientAge >= minimumEligibleAge;
}

export function getHideSinglePayAnd10Pay(
  hideSinglePayAnd10PayFlag: boolean | undefined,
  quoteApplyNewYorkLifeFlag: boolean | undefined,
  clientBirthDate: string | Date,
) {
  const clientAge = calculateAge(new Date(clientBirthDate));
  const minimumEligibleAge = quoteApplyNewYorkLifeFlag
    ? MIN_ELIGIBLE_AGE_NYL_10PAY_SINGLE_PAY
    : MIN_ELIGIBLE_AGE_10PAY_SINGLE_PAY;
  return !!hideSinglePayAnd10PayFlag || clientAge < minimumEligibleAge;
}
